import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { FireIcon, RocketLaunchIcon, XMarkIcon, Bars3Icon, BeakerIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { FaXTwitter, FaTelegram, FaChartLine } from 'react-icons/fa6';
import { motion } from 'framer-motion';
import { ethers } from 'ethers';
import './utils/buffer-polyfill';
import FlameMatrix from './components/FlameMatrix';
import CursorTrail from './components/CursorTrail';
import TokenomicsChart from './components/TokenomicsChart';
import CountdownOverlay from './components/CountdownOverlay';
import LoadingAnimation from './components/LoadingAnimation';
import { SpeedInsights } from "@vercel/speed-insights/next";

// Lazy load the TeamSection to prevent it from affecting initial load
const TeamSection = React.lazy(() => import('./components/TeamSection'));

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [burnedTokens, setBurnedTokens] = useState('0');
  const [isLoading, setIsLoading] = useState(true);
  const [topHolders, setTopHolders] = useState([]);
  const [totalSupply, setTotalSupply] = useState('0');
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [burnStats, setBurnStats] = useState({
    last24h: '0',
    last48h: '0',
    last72h: '0'
  });
  const [burnLeaderboard, setBurnLeaderboard] = useState([]);
  const [burnLeaderboardLoading, setBurnLeaderboardLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(true); // Re-enabled countdown overlay

  const BURN_WALLET = '0x000000000000000000000000000000000000dead';
  const TOKEN_CONTRACT = '0xA6D7137aF64280e3eb8715aB6766740984Dd35E7';
  const DOGECHAIN_RPC = 'https://rpc.dogechain.dog';
  const DOGESHREK_LINK = 'https://dogeshrek.com/';
  const TELEGRAM_LINK = 'https://t.me/fuckPEPEonDC';
  const CHART_LINK = 'https://dexscreener.com/dogechain/0xd31d96be6a2414600fdba03e11f43833a362dd53?embed=1&theme=dark&trades=1&defaultPane=trades';
  const TWITTER_LINK = 'https://twitter.com/FUCKPEPE_DC';
  const DEX_SCREENER_LINK = 'https://dexscreener.com/dogechain/0xd31d96be6a2414600fdba03e11f43833a362dd53';
  const BLOCKSCOUT_API = 'https://explorer.dogechain.dog/api';
  const EXPLORER_URL = 'https://explorer.dogechain.dog/address/';

  const FOUNDATION_ADDRESS = '0x73B56Aa56b35A9EB062ebB0e187fE0f2603DBd25'.toLowerCase();
  const WOJAK_LP_ADDRESS = '0xDcA31b979661E5752AFd4B878F4F301B2cBeFe58'.toLowerCase();
  const TEAM_WALLET_ADDRESS = '0x3ad9b46A09F65e1eE9a3A80e5E097c80D2897E5F'.toLowerCase();
  const MARKETING_ADDRESS = '0x6B3595068778DD592e39A122f4f5a5cF09C90fE2'.toLowerCase();

  const LABELED_ADDRESSES = {
    [FOUNDATION_ADDRESS]: { name: 'Dogechain Foundation', tag: '🏛️ Foundation' },
    [WOJAK_LP_ADDRESS]: { name: 'Wojak LPs (Wojk-LP)', tag: '💧 Liquidity' },
    [BURN_WALLET.toLowerCase()]: { name: 'Burn Wallet ', tag: '🔥 Burned' },
    [TEAM_WALLET_ADDRESS]: { name: 'Team Wallet', tag: '👥 Team' },
    [MARKETING_ADDRESS]: { name: 'Marketing', tag: '📢 Marketing' },
    ['0xd31D96BE6A2414600fdbA03E11F43833a362DD53'.toLowerCase()]: { name: 'DogeShrek Liquidity Token', tag: '💧 Liquidity' }
  };

  // ABI for token balance and supply check
  const minABI = [
    {
      constant: true,
      inputs: [{ name: "_owner", type: "address" }],
      name: "balanceOf",
      outputs: [{ name: "balance", type: "uint256" }],
      type: "function",
    },
    {
      constant: true,
      inputs: [],
      name: "decimals",
      outputs: [{ name: "", type: "uint8" }],
      type: "function",
    },
    {
      constant: true,
      inputs: [],
      name: "totalSupply",
      outputs: [{ name: "", type: "uint256" }],
      type: "function",
    },
    {
      anonymous: false,
      inputs: [
        { indexed: true, name: "from", type: "address" },
        { indexed: true, name: "to", type: "address" },
        { indexed: false, name: "value", type: "uint256" }
      ],
      name: "Transfer",
      type: "event"
    }
  ];

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes synchronizedGlow {
        0%, 100% {
          filter: drop-shadow(0 0 4px #FF4800);
        }
        50% {
          filter: drop-shadow(0 0 8px #FF4800);
        }
      }

      @keyframes textShimmer {
        0%, 100% {
          color: #FF4800;
        }
        50% {
          color: #FFD700;
        }
      }

      .text-glow-sync {
        animation: synchronizedGlow 2s ease-in-out infinite, textShimmer 3s ease-in-out infinite;
      }

      @keyframes burnGlow {
        0%, 100% {
          text-shadow: 0 0 5px #FF4800, 0 0 10px #FF4800;
        }
        50% {
          text-shadow: 0 0 8px #FF4800, 0 0 15px #FF4800;
        }
      }
      
      .burn-glow {
        animation: burnGlow 3s ease-in-out infinite;
      }

      .running-animation {
        animation-play-state: running;
      }

      .no-transform {
        transform: none;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    // Add smooth scroll behavior to HTML element
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const fetchBurnLeaderboard = useCallback(async () => {
    try {
      setBurnLeaderboardLoading(true);
      const provider = new ethers.providers.JsonRpcProvider(DOGECHAIN_RPC);
      const contract = new ethers.Contract(TOKEN_CONTRACT, minABI, provider);
      
      // Get total burned amount first
      const totalBurned = await contract.balanceOf(BURN_WALLET);
      
      // Get burn address transactions for the last 7 days to ensure we have enough data
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = currentBlock - (43200 * 7); // Last 7 days of blocks (2s block time)
      
      const response = await fetch(
        `${BLOCKSCOUT_API}?module=account&action=tokentx&address=${BURN_WALLET}&startblock=${fromBlock}&endblock=${currentBlock}&contractaddress=${TOKEN_CONTRACT}&page=1&offset=1000`
      );
      const data = await response.json();
      
      if (!data.result || !Array.isArray(data.result)) {
        throw new Error('Invalid response from API');
      }
      
      // Process transactions by address
      const burnsByAddress = {};
      for (const tx of data.result) {
        const burner = tx.from.toLowerCase();
        if (burner === BURN_WALLET.toLowerCase()) continue;
        
        if (!burnsByAddress[burner]) {
          burnsByAddress[burner] = {
            address: burner,
            burnedAmount: ethers.BigNumber.from(0),
            lastBurn: 0
          };
        }
        
        burnsByAddress[burner].burnedAmount = burnsByAddress[burner].burnedAmount.add(tx.value);
        burnsByAddress[burner].lastBurn = Math.max(burnsByAddress[burner].lastBurn, parseInt(tx.timeStamp));
      }
      
      // Convert to array and sort by burned amount
      const sortedBurners = Object.entries(burnsByAddress)
        .map(([address, data]) => ({
          address,
          burnedAmount: data.burnedAmount,
          lastBurn: data.lastBurn,
          tag: LABELED_ADDRESSES[address]?.tag || '👤 Holder',
          percentOfBurns: totalBurned.gt(0) 
            ? (data.burnedAmount.mul(1000000).div(totalBurned).toNumber() / 10000).toFixed(4)
            : '0.0000'
        }))
        .sort((a, b) => {
          if (b.burnedAmount.eq(a.burnedAmount)) return 0;
          return b.burnedAmount.gt(a.burnedAmount) ? 1 : -1;
        })
        .slice(0, 10);
      
      setBurnLeaderboard(sortedBurners);
    } catch (error) {
      console.error('Error fetching burn leaderboard:', error);
      setBurnLeaderboard([]);
    } finally {
      setBurnLeaderboardLoading(false);
    }
  }, []);

  const fetchBurnedTokens = async () => {
    try {
      setIsLoading(true);
      const provider = new ethers.providers.JsonRpcProvider(DOGECHAIN_RPC);
      const contract = new ethers.Contract(TOKEN_CONTRACT, minABI, provider);
      const balance = await contract.balanceOf(BURN_WALLET);
      setBurnedTokens(balance.toString()); // Store as string to preserve precision
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching burned tokens:', error);
      setIsLoading(false);
    }
  };

  const fetchLeaderboardData = async () => {
    try {
      setLeaderboardLoading(true);
      
      // Fetch top holders
      const holdersResponse = await fetch(`${BLOCKSCOUT_API}?module=token&action=getTokenHolders&contractaddress=${TOKEN_CONTRACT}&page=1&offset=20`);
      const holdersData = await holdersResponse.json();
      
      if (holdersData.status === '1' && holdersData.result) {
        // Filter out burn wallet and sort by balance
        const holders = holdersData.result
          // First sort by balance
          .map(holder => {
            const balance = ethers.BigNumber.from(holder.value || '0');
            return {
              address: holder.address,
              balance: balance.toString()
            };
          })
          .sort((a, b) => ethers.BigNumber.from(b.balance).gt(ethers.BigNumber.from(a.balance)) ? 1 : -1)
          // Take top 15 initially to ensure we have enough after filtering
          .slice(0, 15)
          // Then filter out burn wallet
          .filter(holder => holder.address.toLowerCase() !== BURN_WALLET.toLowerCase())
          // Finally take top 10
          .slice(0, 10);

        setTopHolders(holders);
      }

      // Fetch total supply
      const provider = new ethers.providers.JsonRpcProvider(DOGECHAIN_RPC);
      const contract = new ethers.Contract(TOKEN_CONTRACT, minABI, provider);
      const totalSupply = await contract.totalSupply();
      setTotalSupply(totalSupply.toString());

      setLeaderboardLoading(false);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      setLeaderboardLoading(false);
      setTopHolders([]);
    }
  };

  const fetchBurnStats = async () => {
    try {
      const provider = new ethers.providers.JsonRpcProvider(DOGECHAIN_RPC);
      const currentBlock = await provider.getBlockNumber();
      const currentBlock24h = currentBlock - 43200; // Approx 24h worth of blocks (2s block time)
      const currentBlock48h = currentBlock - 86400; // 48h worth of blocks
      const currentBlock72h = currentBlock - 129600; // 72h worth of blocks
      
      // Get burn address transactions
      const burnAddress = '0x000000000000000000000000000000000000dead';
      const apiUrl = 'https://explorer.dogechain.dog/api';
      
      const [burns24h, burns48h, burns72h] = await Promise.all([
        fetch(`${apiUrl}?module=account&action=tokentx&address=${burnAddress}&startblock=${currentBlock24h}&endblock=${currentBlock}&contractaddress=${TOKEN_CONTRACT}`).then(res => res.json()),
        fetch(`${apiUrl}?module=account&action=tokentx&address=${burnAddress}&startblock=${currentBlock48h}&endblock=${currentBlock}&contractaddress=${TOKEN_CONTRACT}`).then(res => res.json()),
        fetch(`${apiUrl}?module=account&action=tokentx&address=${burnAddress}&startblock=${currentBlock72h}&endblock=${currentBlock}&contractaddress=${TOKEN_CONTRACT}`).then(res => res.json())
      ]);

      // Calculate total burns for each period
      const calculateTotalBurns = (transactions) => {
        if (!transactions.result || !Array.isArray(transactions.result)) return '0';
        return transactions.result.reduce((total, tx) => {
          return ethers.BigNumber.from(total).add(ethers.BigNumber.from(tx.value)).toString();
        }, '0');
      };

      setBurnStats({
        last24h: calculateTotalBurns(burns24h),
        last48h: calculateTotalBurns(burns48h),
        last72h: calculateTotalBurns(burns72h)
      });
    } catch (error) {
      console.error('Error fetching burn stats:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchBurnedTokens(),
          fetchBurnStats(),
          fetchLeaderboardData(),
          fetchBurnLeaderboard()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setIsLoading(false);
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Fetch every 30 seconds
    return () => clearInterval(interval);
  }, [fetchBurnLeaderboard]);

  useEffect(() => {
    fetchBurnLeaderboard();
    const interval = setInterval(fetchBurnLeaderboard, 30000); // Fetch every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Utility function to format balance in trillions
  const formatBalance = (balance, fullNumber = false) => {
    try {
      if (!balance) return fullNumber ? '0' : '0.00 T';

      // Convert the balance to a number using ethers.js
      const rawNumber = ethers.utils.formatEther(balance);
      const number = parseFloat(rawNumber);

      if (fullNumber) {
        // Multiply by 1e9 to get back to actual token amount (since formatEther divides by 1e18)
        const fullAmount = number * 1000000000; // 1e9
        // Format with commas for full number display
        return fullAmount.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }

      // For leaderboard display, show in trillions
      const inTrillions = number / 1000; // Convert from billions to trillions
      return `${inTrillions.toFixed(1)} T`;
    } catch (error) {
      console.error('Error formatting balance:', error, 'for balance:', balance);
      return fullNumber ? '0' : '0.00 T';
    }
  };

  // Utility function to calculate percentages
  const calculatePercentage = (balance, total) => {
    try {
      const balanceNum = ethers.utils.formatEther(balance || '0');
      const totalNum = ethers.utils.formatEther(total || '0');
      return ((parseFloat(balanceNum) / parseFloat(totalNum)) * 100).toFixed(2);
    } catch (error) {
      console.error('Error calculating percentage:', error);
      return '0.00';
    }
  };

  // Handle smooth scrolling for anchor links
  const handleSmoothScroll = (e, targetId) => {
    if (targetId.startsWith('#')) {
      e.preventDefault();
      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        const headerOffset = 100; // Account for fixed header
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Close mobile menu if open
        setIsMenuOpen(false);
      }
    }
  };

  // Dead simple button with hover effect
  const Button = ({ children, href, target, onClick, icon: Icon, className = '' }) => (
    <a 
      href={href}
      target={target}
      onClick={(e) => {
        if (href?.startsWith('#')) {
          handleSmoothScroll(e, href);
        }
        onClick?.(e);
      }}
      className={`inline-flex items-center gap-2 rounded-full border-2 border-[#FF4800] bg-transparent text-[#FF4800] hover:bg-[#FF4800] hover:text-white px-6 py-2 font-bold transition-all duration-300 ${className}`}
    >
      {Icon && <Icon className="w-5 h-5" />}
      {children}
    </a>
  );

  // Loading Animation Component
  const LoadingAnimation = () => (
    <div className="flex justify-center items-center py-8">
      <motion.div
        animate={{ 
          rotate: [0, 360],
          scale: [1, 0.9, 1]
        }}
        transition={{ 
          rotate: { duration: 2, repeat: Infinity, ease: "linear" },
          scale: { duration: 1, repeat: Infinity, repeatType: "reverse" }
        }}
        className="w-8 h-8"
      >
        <FireIcon className="text-[#FF4800] w-8 h-8" />
      </motion.div>
    </div>
  );

  // Team section component
  const TeamSectionComponent = () => (
    <Suspense fallback={<LoadingAnimation />}>
      <TeamSection />
    </Suspense>
  );

  return (
    <div className="min-h-screen w-full bg-black flex flex-col">
      <div id="top"></div>
      <FlameMatrix />
      <CursorTrail />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-orange-500/20">
        <div className="max-w-6xl mx-auto w-full px-4">
          <div className="flex items-center justify-between h-20 relative">
            {/* Mobile Menu Toggle */}
            <Button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              icon={isMenuOpen ? XMarkIcon : Bars3Icon}
              className="lg:hidden absolute left-0"
            >
              {isMenuOpen ? 'Close' : 'Menu'}
            </Button>

            {/* Brand for mobile */}
            <div className="lg:hidden flex items-center justify-end w-full">
              <div className="flex items-center cursor-pointer" onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                <span className="text-2xl font-bold text-[#FF4800] text-glow-sync">$FUCKPEPE</span>
                <FireIcon className="w-8 h-8 text-[#FF4800] animate-burn text-glow-sync" />
              </div>
            </div>

            {/* Desktop Brand */}
            <div className="hidden lg:flex items-center cursor-pointer" onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              <FireIcon className="w-10 h-10 text-[#FF4800] animate-burn text-glow-sync mr-2" />
              <span className="text-2xl font-bold text-[#FF4800] text-glow-sync">$FUCKPEPE</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4">
              {[
                { text: 'Home', href: '#top' },
                { text: 'About', href: '#about' },
                { text: 'Tokenomics', href: '#tokenomics' },
                { text: 'Chart', href: '#chart' },
                { text: 'Team', href: '#team' },
                { text: 'Roadmap', href: '#roadmap' },
                { text: 'FAQ', href: '#faq' }
              ].map(({ text, href }) => (
                <Button 
                  key={text} 
                  href={href} 
                  target={href.startsWith('#') ? '_self' : '_blank'}
                  rel={href.startsWith('#') ? '' : 'noopener noreferrer'}
                >
                  {text}
                </Button>
              ))}
            </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
              <div className="lg:hidden fixed inset-x-0 top-20 bg-black/95 backdrop-blur-sm border-b border-orange-500/20">
                <div className="flex flex-col items-center py-6 space-y-4">
                  {[
                    { text: 'Home', href: '#top' },
                    { text: 'About', href: '#about' },
                    { text: 'Tokenomics', href: '#tokenomics' },
                    { text: 'Chart', href: '#chart' },
                    { text: 'Team', href: '#team' },
                    { text: 'Roadmap', href: '#roadmap' },
                    { text: 'FAQ', href: '#faq' }
                  ].map(({ text, href }) => (
                    <Button
                      key={text}
                      href={href}
                      target={href.startsWith('#') ? '_self' : '_blank'}
                      rel={href.startsWith('#') ? '' : 'noopener noreferrer'}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {text}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {isLocked ? (
        <div className="min-h-screen flex flex-col relative">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <main className="flex-1 relative z-10">
            <CountdownOverlay onUnlock={() => setIsLocked(false)} />
          </main>
          <footer className="fixed bottom-0 left-0 right-0 z-[9999] bg-black/80 backdrop-blur-sm border-t border-orange-500/20 py-4 pointer-events-auto">
            <div className="w-full max-w-6xl mx-auto px-6 flex justify-center space-x-6">
              <a href={TWITTER_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                <FaXTwitter className="w-6 h-6 text-glow-sync" />
              </a>
              <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                <FaTelegram className="w-6 h-6 text-glow-sync" />
              </a>
              <a href={CHART_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                <FaChartLine className="w-6 h-6 text-glow-sync" />
              </a>
            </div>
          </footer>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 w-full">
          <div className="space-y-16">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20">
              <FlameMatrix />
              <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <span className="text-6xl">🐕/🐸</span>
                  </div>
                  <h1 className="text-4xl font-bold mb-6 text-[#FF4800] text-glow-sync">
                    DOGS OVER FROGS 🔥
                  </h1>
                  <div className="text-lg text-white/80 max-w-3xl mx-auto space-y-6">
                    <p className="font-bold text-xl text-white">
                      $FUCKPEPE: The deflationary memecoin that's setting Dogechain ablaze! 🔥
                    </p>
                    <p>
                      $FUCKPEPE is a community-driven powerhouse, born from rebellion and destined to disrupt. With a mission to topple $PEPE's reign, it's time to rally behind the ultimate meme uprising.
                    </p>
                    <p>
                      Join the movement and watch as $FUCKPEPE blazes its trail across Dogechain!
                    </p>
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex justify-center space-x-6">
                  <Button 
                    href={TELEGRAM_LINK} 
                    target="_blank"
                  >
                    Join Community
                  </Button>
                </div>
              </div>
            </section>

            {/* About Section */}
            <section className="py-20 bg-black/30 relative" id="about">
              <div className="container mx-auto px-4">
                <div className="text-center mb-4">
                  <motion.div
                    className="text-6xl mb-4"
                    animate={{ 
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 0.9, 1]
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    📖
                  </motion.div>
                  <h2 className="text-4xl font-bold text-[#FF4800] text-glow-sync">
                    ABOUT FUCKPEPE
                  </h2>
                </div>
                <div className="prose prose-lg prose-invert mx-auto max-w-4xl">
                  <p className="mb-6">Welcome to the chaotic, hilarious, and revolutionary world of FUCKPEPE—the anti-hero of memecoin's. Born to disrupt, entertain, and dominate. FUCKPEPE isn't just another frog; he's the anti-PEPE renegade created on Dogechain, the O.G. Chain of Memes and Dreams, with one mission: to bring memecoin season back where it belongs. Our battle cry? "DOGS OVER FROGS."</p>
                  
                  <p className="mb-6">Launched on May 1, 2023, just after 4:20 PM (because, of course), FUCKPEPE was born out of frustration with the endless PEPE clones hogging the spotlight and wrecking the charts. The creator, a die-hard Dogecoin fan, decided to flip the script and give the community a fair launch token that's as rebellious as it is rewarding. No pre-sales, no gimmicks—just a simple token address dropped in the Dogechain Telegram channel, sparking a movement.</p>
                  
                  <p className="mb-6">FUCKPEPE's deflationary tokenomics are designed to keep the chaos controlled. With every buy, sell, or transfer, we burn tokens, stack LP, and reward holders with reflections. This model not only stabilizes the chart but also builds higher floors, reduces sell pressure, and increases scarcity. It's a system built to thrive where others fail—because unlike the original PEPE, we're here to reward loyalty and ensure sustainability.</p>
                  
                  <p className="mb-6">Within 22 hours of launch, FUCKPEPE skyrocketed over 21,000%, making history as the first real token on Dogechain to hit such insane gains in such a short time. From there, a community of degens, memerlords, and crypto rebels united to bring structure, utility, and unstoppable energy to FUCKPEPE.</p>
                  
                  <p className="mb-6">Whether you love PEPE or hate it, FUCKPEPE is your ultimate hedge. If PEPE tanks, swap it for FUCKPEPE. If PEPE moons and you missed the ride, grab FUCKPEPE instead. Because here, we don't just follow trends—we create them.</p>
                  
                  <p className="mb-6">Join the rebellion. Embrace the chaos. And remember, it's always "DOGS OVER FROGS."</p>
                </div>
                <div className="text-center mt-12 text-xl text-white/80">
                  Dive into the deflationary force that's reigniting the memecoin spirit 👇
                </div>
              </div>
            </section>

            {/* Tokenomics Section */}
            <section className="py-20 bg-black/30 relative" id="tokenomics">
              <div className="container mx-auto px-4">
                <div className="text-center mb-4">
                  <motion.div
                    className="text-6xl mb-4"
                    animate={{ 
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 0.9, 1]
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    📊
                  </motion.div>
                  <h2 className="text-4xl font-bold text-[#FF4800] text-glow-sync">
                    TOKENOMICS
                  </h2>
                </div>
                <div className="prose prose-lg prose-invert mx-auto max-w-4xl text-center">
                  <p className="mb-6">$FUCKPEPE's tokenomics are as rebellious as its name.</p>
                  <p className="mb-6">Featuring a deflationary model that automatically activates with every transaction, it strengthens the chart by increasing liquidity and reducing supply through burns.</p>
                  <p className="mb-6">This ensures not just a healthy chart, but also rewards holders with reflections.</p>
                  <p className="mb-6">Unlike its predecessors, $FUCKPEPE offers stability and scarcity, making it a pioneer on Dogechain.</p>
                  <p className="mb-6">Experience the thrill of a token that's designed for growth and resilience, ensuring everyone can $FUCKPEPE their way to success.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  {[
                    { 
                      title: '2% Liquidity', 
                      description: 'Automatically added to LP, creating a stronger floor',
                      icon: BeakerIcon
                    },
                    { 
                      title: '2% Burn', 
                      description: `Permanently removed from circulation, increasing scarcity. Total burned: ${isLoading ? '...' : formatBalance(burnedTokens)}`,
                      icon: FireIcon
                    },
                    { 
                      title: '1% Reflections', 
                      description: 'Rewarded back to diamond-handed holders',
                      icon: ArrowPathIcon
                    }
                  ].map(({ title, description, icon: Icon }) => (
                    <motion.div
                      key={title}
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 0 }}
                      whileHover={{ 
                        scale: 1.02,
                        rotate: 4,
                        zIndex: 10,
                        transition: { duration: 0.2 }
                      }}
                      className="h-full no-transform"
                    >
                      <div className="bg-fuckpepe-dark p-6 rounded-xl border-2 border-fuckpepe-fire/50 text-center shadow-lg hover:shadow-lg">
                        <Icon className="w-12 h-12 mx-auto text-fuckpepe-fire mb-4" />
                        <h4 className="text-2xl font-bold mb-2 text-fuckpepe-fire">{title}</h4>
                        <p className="text-white/80">{description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Burned Tokens Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mb-8"
                >
                  <div className="px-4 py-8 backdrop-blur-sm border-2 border-[#FF4800]/20 rounded-lg shadow-lg hover:shadow-lg transition-shadow duration-300">
                    <div className="flex justify-center items-center">
                      {isLoading ? (
                        <LoadingAnimation />
                      ) : (
                        <div className="text-center">
                          <div className="text-4xl font-bold mb-2 text-[#FF4800] text-glow-sync running-animation">
                            <span key="burned-amount">{formatBalance(burnedTokens, true)}</span>
                          </div>
                          <div className="text-white/60">
                            Total Tokens Burned Forever ({calculatePercentage(burnedTokens, totalSupply)}% of Total Supply)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Additional Burn Statistics */}
                <div className="mt-8">
                  <div className="grid md:grid-cols-3 gap-8">
                    {[
                      { 
                        title: 'Last 24 Hours', 
                        amount: burnStats.last24h,
                        subtitle: 'Tokens Burned'
                      },
                      { 
                        title: 'Last 48 Hours',
                        amount: burnStats.last48h,
                        subtitle: 'Tokens Burned'
                      },
                      { 
                        title: 'Last 72 Hours',
                        amount: burnStats.last72h,
                        subtitle: 'Tokens Burned'
                      }
                    ].map(({ title, amount, subtitle }) => (
                      <motion.div
                        key={title}
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 0 }}
                        whileHover={{ 
                          scale: 1.02,
                          rotate: 4,
                          zIndex: 10,
                          transition: { duration: 0.2 }
                        }}
                        className="h-full no-transform"
                      >
                        <div className="bg-fuckpepe-dark p-6 rounded-xl border-2 border-fuckpepe-fire/50 text-center shadow-lg hover:shadow-lg">
                          <h4 className="text-xl mb-2 text-white">{title}</h4>
                          {isLoading ? (
                            <LoadingAnimation />
                          ) : (
                            <div className="text-center">
                              <div className="text-3xl font-bold mb-2 text-[#FF4800] text-glow-sync running-animation">
                                <span key={`burned-${title}`}>{formatBalance(amount, true)}</span>
                              </div>
                              <p className="text-white/60">{subtitle}</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tokenomics Simulation Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mt-8"
                >
                  <TokenomicsChart />
                </motion.div>
              </div>
            </section>

            {/* Leaderboard Section */}
            <section className="relative z-10 w-full text-center mb-16">
              <div className="flex flex-col items-center justify-center mb-8">
                <motion.div 
                  className="text-6xl mb-4"
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 0.9, 1]
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  🏆
                </motion.div>
                <h2 className="text-4xl font-bold mb-6 text-[#FF4800] text-glow-sync">
                  TOP HOLDERS LEADERBOARD
                </h2>
              </div>
              
              <div className="w-full">
                <div className="px-4 py-8 backdrop-blur-sm border-2 border-[#FF4800]/20 rounded-lg shadow-lg hover:shadow-lg transition-shadow duration-300">
                  <div className="overflow-hidden">
                    {leaderboardLoading ? (
                      <LoadingAnimation />
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#FF4800]/20">
                              <th className="py-2 pl-4 text-left text-white">#</th>
                              <th className="py-2 pl-8 text-left text-white">Address</th>
                              <th className="py-2 pl-8 text-left text-white">Tag</th>
                              <th className="py-2 pl-8 text-left text-white">Balance</th>
                              <th className="py-2 pl-8 text-left text-white">% of Total</th>
                              <th className="py-2 pl-8 text-left text-white">% of Circ.</th>
                              <th className="py-2 pl-8 text-left text-white">% Gained</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topHolders.map((holder, index) => {
                              const displayName = LABELED_ADDRESSES[holder.address.toLowerCase()]?.name || 
                                `${holder.address.slice(0, 6)}...${holder.address.slice(-4)}`;
                              const tag = LABELED_ADDRESSES[holder.address.toLowerCase()]?.tag || '👤 Holder';
                              const balance = ethers.utils.formatUnits(holder.balance, 9);
                              const percentOfTotal = ((Number(balance) / Number(ethers.utils.formatUnits(totalSupply, 9))) * 100).toFixed(2);
                              const percentOfCirc = ((Number(balance) / (Number(ethers.utils.formatUnits(totalSupply, 9)) - Number(ethers.utils.formatUnits(burnedTokens, 9)))) * 100).toFixed(2);
                              
                              return (
                                <tr key={holder.address} className="border-b border-[#FF4800]/10">
                                  <td className="py-2 pl-4 text-left font-bold">#{index + 1}</td>
                                  <td className="py-2 pl-8 text-left">
                                    <a
                                      href={`${EXPLORER_URL}${holder.address}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#FF4800]"
                                    >
                                      {displayName}
                                    </a>
                                  </td>
                                  <td className="py-2 pl-8 text-left">{tag}</td>
                                  <td className="py-2 pl-8 text-left">
                                    {Number(balance).toLocaleString(undefined, {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 1
                                    })}
                                  </td>
                                  <td className="py-2 pl-8 text-left">
                                    <span className="text-[#FF4800] text-glow-sync">{percentOfTotal}%</span>
                                  </td>
                                  <td className="py-2 pl-8 text-left">
                                    <span className="text-[#FF4800] text-glow-sync">{percentOfCirc}%</span>
                                  </td>
                                  <td className="py-2 pl-8 text-left">
                                    <span className="text-[#FF4800] text-glow-sync">+0.00%</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Burn Leaderboard */}
            <section className="relative z-10 w-full text-center mb-16">
              <div className="flex flex-col items-center justify-center mb-8">
                <motion.div 
                  className="text-6xl mb-4"
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 0.9, 1]
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  🏆
                </motion.div>
                <h2 className="text-4xl font-bold mb-6 text-[#FF4800] text-glow-sync">
                  BURN LEADERBOARD
                </h2>
              </div>
              
              <div className="w-full">
                <div className="px-4 py-8 backdrop-blur-sm border-2 border-[#FF4800]/20 rounded-lg shadow-lg hover:shadow-lg transition-shadow duration-300">
                  <div className="overflow-hidden">
                    {burnLeaderboardLoading ? (
                      <LoadingAnimation />
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#FF4800]/20">
                              <th className="py-2 pl-4 text-left text-white">#</th>
                              <th className="py-2 pl-8 text-left text-white">Address</th>
                              <th className="py-2 pl-8 text-left text-white">Tag</th>
                              <th className="py-2 pl-8 text-left text-white">Last Burn</th>
                              <th className="py-2 pl-8 text-left text-white">Burned Amount</th>
                              <th className="py-2 pl-8 text-left text-white">% of Burns</th>
                            </tr>
                          </thead>
                          <tbody>
                            {burnLeaderboard.map((entry, index) => {
                              let displayName = entry.address;
                              if (LABELED_ADDRESSES[entry.address.toLowerCase()]) {
                                displayName = LABELED_ADDRESSES[entry.address.toLowerCase()].name;
                              } else {
                                displayName = `${entry.address.slice(0, 6)}...${entry.address.slice(-4)}`;
                              }

                              return (
                                <tr key={entry.address} className="border-b border-[#FF4800]/10">
                                  <td className="py-2 pl-4 text-left font-bold">#{index + 1}</td>
                                  <td className="py-2 pl-8 text-left">
                                    <a
                                      href={`${EXPLORER_URL}${entry.address}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#FF4800]"
                                    >
                                      {displayName}
                                    </a>
                                  </td>
                                  <td className="py-2 pl-8 text-left">{entry.tag}</td>
                                  <td className="py-2 pl-8 text-left text-white">
                                    {new Date(entry.lastBurn * 1000).toLocaleDateString()}
                                  </td>
                                  <td className="py-2 pl-8 text-left">
                                    {Number(ethers.utils.formatUnits(entry.burnedAmount, 9)).toLocaleString(undefined, {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 1
                                    })}
                                  </td>
                                  <td className="py-2 pl-8 text-left">
                                    <span className="text-[#FF4800] text-glow-sync">
                                      {entry.percentOfBurns}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Chart Section */}
            <section id="chart" className="relative z-10 w-full text-center">
              <motion.div 
                className="text-6xl mb-2"
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 0.9, 1]
                }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                📈
              </motion.div>
              <h2 className="text-4xl font-bold mb-6 text-[#FF4800] text-glow-sync">
                CHART
              </h2>
              <div className="mb-6">
                <iframe 
                  src={CHART_LINK}
                  width="100%"
                  height="600"
                  title="DEX Screener Chart"
                  className="border-2 border-fuckpepe-fire/50 rounded-xl relative z-10"
                  allow="clipboard-write"
                />
              </div>

              {/* Contract Address */}
              <div className="max-w-6xl mx-auto px-4 mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-fuckpepe-dark/50 backdrop-blur-sm p-4 rounded-xl border-2 border-fuckpepe-fire/30"
                >
                  <p className="text-white/80 mb-2">Contract Address:</p>
                  <a
                    href="https://explorer.dogechain.dog/token/0xA6D7137aF64280e3eb8715aB6766740984Dd35E7/token-transfers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF4800] hover:text-[#FF4800]/80 text-glow-sync break-all font-mono transition-colors duration-200"
                  >
                    0xA6D7137aF64280e3eb8715aB6766740984Dd35E7
                  </a>
                </motion.div>
              </div>

              <motion.a
                href={DOGESHREK_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-12 py-6 text-2xl font-bold text-white bg-gradient-to-r from-[#FF4800] to-[#FF6B00] rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-[#FF4800]/50 hover:text-white"
                whileHover={{ 
                  scale: 1.05,
                  textShadow: "0 0 8px rgb(255, 255, 255)",
                  boxShadow: "0 0 20px rgb(255, 72, 0)"
                }}
                whileTap={{ scale: 0.95 }}
              >
                BUY $FUCKPEPE NOW 🚀
              </motion.a>
            </section>

            {/* Team Section */}
            <section id="team" className="relative z-10 w-full text-center mb-16">
              <motion.div 
                className="text-6xl mb-2"
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 0.9, 1]
                }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                🐕
              </motion.div>
              <h2 className="text-4xl font-bold mb-8 text-[#FF4800] text-glow-sync">
                TEAM
              </h2>
              <Suspense fallback={<LoadingAnimation />}>
                <TeamSection />
              </Suspense>
            </section>

            {/* Roadmap Section */}
            <section id="roadmap" className="relative z-10 w-full text-center mb-16">
              <div className="flex flex-col items-center justify-center mb-8">
                <motion.div 
                  className="text-6xl mb-4"
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 0.9, 1]
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  🗺️
                </motion.div>
                <h2 className="text-4xl font-bold mb-12 text-[#FF4800] text-glow-sync">
                  ROADMAP
                </h2>
                <div className="relative max-w-6xl mx-auto px-4">
                  {/* Vertical line connecting phases */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FF4800] via-[#FF6B00] to-[#FF4800] transform -translate-x-1/2 rounded-full" />
                  
                  {[
                    {
                      phase: '$FUCKPEPE Goes Full Degen Genius',
                      status: 'in-progress',
                      progress: 0,
                      items: [
                        'Launching the $FUCKPEPE AI chatbot: because even bots need a sense of humor 🤖🐸',
                        'Integrating $FUCKPEPE AI with Twitter: memeing at the speed of light 🚀',
                        '$FUCKPEPE Roulette TG chat game: spin it, win it, or just laugh at your losses 🎰',
                        'Community votes for the 404 NFT collection artist: democracy, but make it chaotic 🎨',
                        'Pitching for Dogechain Ecosystem Grant Funds: because why not get paid to disrupt? 💰'
                      ],
                      emoji: '🎉'
                    },
                    {
                      phase: 'Phase 2: Leveling Up the Chaos',
                      status: 'upcoming',
                      progress: 0,
                      items: [
                        'DEXscreener Listing: so you can track our moon mission in real-time 📈',
                        '$FUCKPEPE Stake-to-Burn Portal: burn it, stake it, love it 🔥',
                        'Developing the FUCKPEPE AI AMM Agent: trading FUCKPEPE and 404 tokens like a true degen bot 🤖💸',
                        'Building the 404 NFT collection & token with burn/mint capabilities: because who doesn’t love a little destruction with creation? 🛠🔥'
                      ],
                      emoji: '🚀'
                    },
                    {
                      phase: 'Phase 3: 404: Sanity Not Found',
                      status: 'upcoming',
                      progress: 0,
                      items: [
                        'Dropping the 404 NFT collection & token: burn it, mint it, flex it 💎🔥',
                        'Deploying the $FUCKPEPE AI AMM Agent: making trades so smooth, even frogs will be jealous 🐸💥'
                      ],
                      emoji: '🤪'
                    },
                    {
                      phase: 'Phase 4: To the Moon and Beyond',
                      status: 'upcoming',
                      progress: 0,
                      items: [
                        'Ride $FUCKPEPE to the moon: because hopping is for frogs, and we’re flying 🌕🚀',
                        'Pro tip: buy more $FUCKPEPE. You’ll thank us later 🐕💸',
                        '#DogsOverFrogs forever, baby. Let’s make chaos profitable 💰'
                      ],
                      emoji: '🌕'
                    }
                  ].map(({ phase, items, emoji, status, progress }, index) => (
                    <motion.div
                      key={phase}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                      className="relative mb-16 last:mb-0"
                    >
                      {/* Phase content */}
                      <div className={`flex flex-col md:flex-row items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                        {/* Content box */}
                        <motion.div 
                          className="w-full md:w-[calc(50%-2rem)] bg-black/50 p-6 md:p-8 rounded-2xl border-2 border-[#FF4800]/20 backdrop-blur-xl relative group"
                          whileHover={{ 
                            scale: 1.02,
                            rotate: index % 2 === 0 ? 2 : -2,
                            transition: { duration: 0.2 }
                          }}
                        >
                          {/* Status indicator */}
                          <div className="flex items-center justify-center mb-4">
                            <motion.span 
                              className="text-3xl md:text-4xl relative"
                              animate={{ 
                                scale: [1, 1.2, 1],
                                rotate: [0, 10, -10, 0]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "reverse"
                              }}
                            >
                              {emoji}
                            </motion.span>
                          </div>

                          <div className={`flex items-center justify-center gap-2 mb-4 ${
                            status === 'completed' ? 'text-green-500' :
                            status === 'in-progress' ? 'text-yellow-500' :
                            'text-gray-500'
                          }`}>
                            <span className="text-sm md:text-base capitalize">{status}</span>
                            <div className="w-2 h-2 rounded-full animate-pulse" style={{
                              backgroundColor: status === 'completed' ? '#22c55e' :
                                             status === 'in-progress' ? '#eab308' :
                                             '#6b7280'
                            }}></div>
                          </div>

                          <h3 className="text-2xl font-bold mb-1 text-[#FF4800]">{phase}</h3>
                          <p className="text-sm text-blue-500 mb-4">{`Q${index + 1}`}</p>

                          {/* Progress bar */}
                          <div className="w-full h-2 bg-black/50 rounded-full mb-6 overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-[#FF4800] to-[#FF6B00]"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${progress}%` }}
                              viewport={{ once: true }}
                              transition={{ duration: 1, delay: 0.5 }}
                            />
                          </div>

                          <ul className="list-disc list-inside text-left">
                            {items.map((item, idx) => (
                              <li key={idx} className="mb-2 text-left text-white">{item}</li>
                            ))}
                          </ul>
                        </motion.div>

                        {/* Timeline node */}
                        <div className="w-16 flex items-center justify-center relative z-10">
                          <motion.div
                            className={`w-8 h-8 rounded-full border-4 ${
                              status === 'completed' ? 'border-green-500 bg-green-500/20' :
                              status === 'in-progress' ? 'border-yellow-500 bg-yellow-500/20' :
                              'border-gray-500 bg-gray-500/20'
                            }`}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                          />
                        </div>

                        {/* Spacer for the other side */}
                        <div className="w-[calc(50%-2rem)]" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="relative z-10 w-full text-center mb-20">
              <div className="flex flex-col items-center justify-center mb-8">
                <motion.div 
                  className="text-6xl mb-4"
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 0.9, 1]
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  ❓
                </motion.div>
                <h2 className="text-4xl font-bold mb-12 text-[#FF4800] text-glow-sync text-center">
                  FAQ
                </h2>
                <div className="grid gap-6 max-w-6xl mx-auto px-4">
                  {[
                    {
                      question: "Why is $FUCKPEPE better than PEPE?",
                      answer: "Because hopping is for frogs, and we’re out here flying with the doges 🐕🚀 Plus, $FUCKPEPE doesn’t just make memes—it makes moves. #DogsOverFrogs"
                    },
                    {
                      question: "What happens if I sell my $FUCKPEPE?",
                      answer: "You’ll hear a faint whisper of disappointment from the Dogechain gods, and your portfolio might start hopping instead of mooning. Don’t be that guy."
                    },
                    {
                      question: "Is $FUCKPEPE a serious project?",
                      answer: "As serious as a frog in a tuxedo riding a rocket. We’re here to disrupt, meme, and dominate—seriously."
                    },
                    {
                      question: "Can $FUCKPEPE really go to the moon?",
                      answer: "The moon? That’s just the first stop. We’re aiming for Pluto, baby 🌌🐸 But remember, DYOR—Do Your Own Research, because every degen's journey is their own risk."
                    },
                    {
                      question: "Why does $FUCKPEPE burn tokens?",
                      answer: "Because nothing says 'we mean business' like setting frogs on fire 🔥 Every burn makes $FUCKPEPE rarer, stronger, and more rebellious."
                    },
                    {
                      question: "What’s the best way to buy $FUCKPEPE?",
                      answer: "With conviction, a sense of humor, and a wallet full of dreams. Oh, and on Dogechain, where the real memecoin magic happens 🐕✨"
                    },
                  ].map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 0 }}
                      whileHover={{ 
                        scale: 1.02,
                        rotate: 4,
                        zIndex: 10,
                        transition: { duration: 0.2 }
                      }}
                      className="relative no-transform"
                    >
                      <div className="bg-fuckpepe-dark p-6 rounded-xl border-2 border-fuckpepe-fire/50 shadow-lg hover:shadow-lg">
                        <h3 className="text-xl font-bold mb-3 text-[#FF4800]">
                          {faq.question}
                        </h3>
                        <p className="text-white/80 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* Legal Disclaimer */}
            <section className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <h3 className="text-sm font-semibold mb-2 text-white/90">
                    LEGAL DISCLAIMER
                  </h3>
                  <p className="text-xs text-white/70 leading-relaxed">
                    $FUCKPEPE is a meme token created for entertainment purposes. This website and its content do not constitute financial advice, investment advice, trading advice, or any other type of advice. $FUCKPEPE makes no representations about the accuracy of this website's information. Cryptocurrency investments are volatile and high-risk. Do not invest more than you can afford to lose. By using this website, you agree that the $FUCKPEPE team is not responsible for any losses or gains that result from your actions. Make your own decisions, do your own research.
                  </p>
                </div>
              </div>
              <div className="mb-20" />
            </section>

            {/* Footer */}
            <footer className="fixed bottom-0 left-0 right-0 z-[9999] bg-black/80 backdrop-blur-sm border-t border-orange-500/20 py-4 pointer-events-auto">
              <div className="w-full max-w-6xl mx-auto flex justify-center space-x-6">
                <a href={TWITTER_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                  <FaXTwitter className="w-6 h-6 text-glow-sync" />
                </a>
                <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                  <FaTelegram className="w-6 h-6 text-glow-sync" />
                </a>
                <a href={CHART_LINK} target="_blank" rel="noopener noreferrer" className="text-[#FF4800] hover:text-white transition-colors">
                  <FaChartLine className="w-6 h-6 text-glow-sync" />
                </a>
              </div>
            </footer>
            <SpeedInsights />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
